// =============================================================================
// selloutOutbox.js — a small DURABLE outbox for sell-out event writes (Phase 3,
// Lane N / module N2). This is the capture-rate reliability fix.
//
// WHY. The sell-out shadow write was fired-and-forgotten from foh.jsx: an
// un-awaited best-effort call whose in-memory retry loop dies the instant the
// screen navigates, unmounts or the tablet sleeps. Chained behind a per-action
// login round-trip, a slow login skipped it entirely. On flaky kiosk wifi all
// three in-memory retries could fail. Result: only 86–94% of sell-outs landed in
// the new system (Meadowhall 67%) — too leaky to ever drop the Sheet safety-net.
//
// WHAT. A tiny localStorage-backed buffer, keyed by idem_key. A tap ENQUEUES its
// intended sell_out_events row SYNCHRONOUSLY (durable before any await — it
// survives an immediate unmount/navigate), then the write is attempted; a
// self-arming flusher drains anything left when connectivity/visibility returns.
//
// WHY THIS IS SAFE where the notifications outbox (#71) deliberately wasn't: a
// notification's day + attribution are derived at flush time (drift). A sell-out
// row carries its event_time, trading_date AND idem_key in the payload at enqueue
// time, and staff_id is stamped explicitly from the login pass — so a replay lands
// the IDENTICAL row, and the unique idem_key index means a replay can only ever
// collapse to the SAME one row (it can never double-write).
//
// Pure of any Supabase import: the actual insert + the activity line are INJECTED
// (the `insert` / `onLanded` seams), so this file is unit-testable with no network
// (tools/lane_n_outbox_test.mjs). It NEVER throws.
// =============================================================================

export const OUTBOX_KEY = "rolled.sellout.outbox.v1";
const MAX_ENTRIES = 200; // a hard cap; a genuinely stuck kiosk drops the OLDEST, logged.

// Storage seam — real localStorage in the app, an injectable Map-like in tests.
function defaultStore() {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch (e) { /* SSR / privacy mode */ }
  // In-memory fallback so nothing throws where localStorage is absent.
  const m = new Map();
  return { getItem: (k) => (m.has(k) ? m.get(k) : null), setItem: (k, v) => m.set(k, v), removeItem: (k) => m.delete(k) };
}
let _store = defaultStore();
export function _setStore(s) { _store = s; } // test seam

function read() {
  try {
    const raw = _store.getItem(OUTBOX_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) { return []; } // corrupt buffer never breaks a tap
}
function write(list) {
  try { _store.setItem(OUTBOX_KEY, JSON.stringify(list)); return true; }
  catch (e) { console.warn("[sellout-outbox] persist failed:", e?.message || e); return false; }
}

// Enqueue (or replace) an entry by idem_key. Synchronous, swallows its own errors,
// returns true if it persisted. `entry` must carry `idem_key`.
export function enqueue(entry) {
  try {
    if (!entry || !entry.idem_key) return false;
    const list = read();
    const i = list.findIndex((e) => e.idem_key === entry.idem_key);
    if (i >= 0) list[i] = { ...list[i], ...entry }; // a double-tap re-enqueues the SAME key -> one entry
    else list.push(entry);
    // Cap: drop the oldest if we somehow run away (a kiosk offline for hours).
    while (list.length > MAX_ENTRIES) {
      const dropped = list.shift();
      console.warn("[sellout-outbox] capacity reached — dropping oldest queued write:", dropped?.idem_key);
    }
    return write(list);
  } catch (e) { console.warn("[sellout-outbox] enqueue threw:", e?.message || e); return false; }
}

export function remove(idemKey) {
  try {
    const list = read().filter((e) => e.idem_key !== idemKey);
    return write(list);
  } catch (e) { return false; }
}

// Stamp the resolved staff_id onto a queued entry (captured after the per-action
// login) so a flushed-later row lands with the RIGHT person, not whoever is logged
// in at flush time.
export function stampStaff(idemKey, staffId) {
  try {
    if (!staffId) return false;
    const list = read();
    const e = list.find((x) => x.idem_key === idemKey);
    if (!e) return false;
    e.staff_id = staffId;
    return write(list);
  } catch (e) { return false; }
}

export function list() { return read(); }
export function size() { return read().length; }

// Drain the outbox. `insert(entry)` -> Promise<{ok, duplicate?}>; a landed or
// duplicate row is removed, a failed one is kept for the next drain. `onLanded`
// (optional) fires per landed row so the activity line follows the SAME success
// path (fired for immediate AND flushed-later rows). Concurrency-guarded so
// overlapping triggers (online + a fresh tap) don't double-drain; a flush
// requested WHILE one is running is COALESCED into one more pass, so a row
// enqueued mid-drain (rapid taps at close) still lands promptly. Never throws.
let _flushing = false, _rerun = false;
export async function flush({ insert, onLanded } = {}) {
  if (typeof insert !== "function") return { landed: 0, kept: read().length, error: "no insert fn" };
  if (_flushing) { _rerun = true; return { landed: 0, kept: read().length, skipped: "coalesced" }; }
  _flushing = true;
  let landed = 0;
  try {
    do {
      _rerun = false;
      const attempted = new Set(); // don't re-hammer a failing entry within one flush
      for (const entry of read()) {
        if (attempted.has(entry.idem_key)) continue;
        attempted.add(entry.idem_key);
        let res;
        try { res = await insert(entry); } catch (e) { res = { ok: false, error: e }; }
        if (res && (res.ok || res.duplicate)) {
          remove(entry.idem_key);
          landed++;
          if (onLanded) { try { onLanded(entry, res); } catch (e) { /* activity line is best-effort */ } }
        }
        // a failure leaves the entry in place for the next flush trigger
      }
    } while (_rerun); // a concurrent flush() during the drain -> one more pass to catch new arrivals
  } finally { _flushing = false; }
  return { landed, kept: read().length };
}
